import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fbt } from 'fbtee'
import { useLocaleContext } from 'fbtee'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { useEventLineups } from '../../hooks/useEventLineups'
import { useFrozenValue } from '../../hooks/useFrozenValue'
import { useEventSuggestions, type SuggestEventSource } from '../../hooks/useEventSuggestions'
import { suggestEventPrompt } from '../../lib/guestPrompts'
import { usePageLoadTrace } from '../../lib/perf'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import { todayLocalISO } from '../../lib/formatDate'
import { getLineupNames } from '../../lib/eventUtils'
import { PageLayout } from '../../components/PageLayout'
import { ErrorState } from '../../components/ErrorState'
import { EmptyState } from '../../components/EmptyState'
import { RegionHeaderChip } from '../../components/RegionHeaderChip'
import { placeholders } from '../../lib/skeletonUtils'
import { EventCard } from '../../components/EventCard'
import { SuggestEventDialog } from '../../components/SuggestEventDialog'
import { SuggestRow } from '../../components/SuggestRow'
import { useBrowseSections } from './useBrowseSections'
import { useListControls, type ListSort } from './useListControls'
import { useSortedList } from './useSortedList'
import { ListControls, TypeControl } from './ListControls'
import { emptyDayText, emptyRangeText, widenRangeLabel } from './listLabels'
import { useEventArtistMatches } from '../../hooks/useEventArtistMatches'
import { usePopularEventIds } from '../../hooks/usePopularEventIds'
import { DATE_RANGES, dateRangeBounds, type DateRange } from '@shared/ranking'
import { RevealList } from './RevealList'
import { HeaderLink } from '../../components/HeaderLink'
import { Button } from '../../components/ui/button'
import { track } from '../../lib/analytics'
import { parseBrowseType, type BrowseType } from './browseType'
import { browseStrings } from './strings'
import type { Event } from '../../types'
import { useDiscoveryFeed } from '../../hooks/useDiscoveryFeed'
import { useRegionDeepLink } from '../../hooks/useRegionDeepLink'
import { SocialEventCard } from '../../components/SocialEventCard'
import { useMinuteNow } from '../../hooks/useMinuteNow'

export default function EventBrowse() {
  const [searchParams, setSearchParams] = useSearchParams()
  const type = parseBrowseType(searchParams.get('type'))
  const s = browseStrings(type)
  useDocumentTitle(pageTitle(fbt('Discover', 'Browser tab title for the Discover page')))

  const {
    user,
    signedInUid,
    authLoading,
    eventStatuses,
    interestsLoading,
    guardedSetEventInterest,
    facepileData,
    mergedProfiles,
    currentUserId,
    explicitFriendIds,
    groups,
    friendInterests,
    socialLoading,
    regionId,
    setRegion,
    isEverywhere,
    regionsLoading,
    eventsLoading: loading,
    eventsPending,
    eventsError: error,
    scopedEvents,
  } = useDiscoveryFeed()
  useRegionDeepLink(setRegion)
  const { locale } = useLocaleContext()
  const openEvent = useOpenEvent()
  const { guard } = useGuestGuard()
  const { suggestEvent } = useEventSuggestions(signedInUid ?? null)

  const [suggestSource, setSuggestSource] = useState<SuggestEventSource | null>(null)
  const openSuggest = guard(
    (source: SuggestEventSource) => setSuggestSource(source),
    suggestEventPrompt(),
  )

  const now = useMinuteNow()
  const today = todayLocalISO()
  // `?date=` narrows the list to one day (from Home's calendar); a past or malformed one is ignored.
  const dateParam = searchParams.get('date')
  const day = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && dateParam >= today ? dateParam : null

  // Regions resolve after events do (venues load on their own), so the freeze
  // has to wait for them — otherwise the snapshot is taken against an empty
  // region map and the region-filtered sections stay stuck on it.
  const ready = !eventsPending && !interestsLoading && !socialLoading && !regionsLoading

  // Discover is for finding something new — events the viewer already marked
  // live on Home. Filtering before the freeze keeps a card in place when the
  // viewer marks it here; it's gone on the next visit.
  const discoverable = useMemo(
    () => scopedEvents.filter(e => {
      const status = eventStatuses.get(e.id)
      return status !== 'interested' && status !== 'going'
    }),
    [scopedEvents, eventStatuses],
  )

  const liveData = useBrowseSections(
    discoverable,
    type,
    friendInterests,
    explicitFriendIds,
    now,
  )
  // The region is user-switchable and the list is region-scoped, so the
  // snapshot has to be dropped when the user picks a different area.
  const { upcoming, crewCounts } = useFrozenValue(
    liveData,
    ready,
    `${user?.uid ?? 'guest'}:${regionId}:${type}`,
  )
  const list = useListControls(type)
  const { matches: artistMatches, matchedArtists, lineupSizes, ready: matchesReady } = useEventArtistMatches(signedInUid ?? null)
  const popularIds = usePopularEventIds(scopedEvents, isEverywhere ? null : regionId, ready, today)
  const bounds = day ? { start: day, end: day } : dateRangeBounds(list.range, today)
  const listed = useSortedList(upcoming, bounds.start, bounds.end, list.sort, artistMatches, isEverywhere ? null : regionId, crewCounts)
  // Relevance with followed artists waits for the matches, so the order
  // doesn't reshuffle under the user when the search index lands.
  const listLoading = list.sort === 'relevance' && !matchesReady
  const widerRange: DateRange | undefined = day ? 'all' : DATE_RANGES[DATE_RANGES.indexOf(list.range) + 1]

  const lineupEvents = useMemo(() => listed.filter(e => e.type === 'party').slice(0, 40), [listed])
  const { lineups } = useEventLineups(lineupEvents)

  usePageLoadTrace('discover_page_to_content', !loading)

  function setType(next: BrowseType) {
    setSearchParams({ ...(next !== 'all' && { type: next }), ...(day && { date: day }) }, { replace: true })
    track('browse_list_changed', { event_type: next, range: list.range, sort: list.sort })
  }

  const filtered = type !== 'all' || list.range !== 'all' || day !== null

  function resetFilters() {
    setSearchParams(params => {
      params.delete('type')
      params.delete('date')
      return params
    }, { replace: true })
    list.update({ range: 'all' })
  }

  function updateList(next: { range?: DateRange; sort?: ListSort }) {
    if (next.range && day) {
      setSearchParams(params => {
        params.delete('date')
        return params
      }, { replace: true })
    }
    list.update(next)
  }

  const card = (event: Event, opts: { withAction?: boolean } = {}) => {
    const withAction = opts.withAction ?? true
    return (
      <SocialEventCard
        key={event.id}
        event={event}
        showType={type === 'all'}
        lineup={getLineupNames(event, lineups)}
        followedArtists={matchedArtists.get(event.id)}
        lineupSize={lineupSizes.get(event.id)}
        popular={popularIds.has(event.id)}
        facepileLoading={socialLoading}
        facepile={facepileData.get(event.id)}
        profiles={mergedProfiles}
        currentUserId={currentUserId}
        locale={locale}
        status={eventStatuses.get(event.id) ?? 'none'}
        statusLoading={authLoading || interestsLoading}
        onOpen={() => openEvent(event, eventStatuses.get(event.id))}
        onSetStatus={withAction ? next => guardedSetEventInterest(event.id, next) : undefined}
      />
    )
  }

  if (error) return <ErrorState error={error} />

  return (
    <PageLayout leading={<RegionHeaderChip />}>
      <div className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none]">
        <TypeControl type={type} onChange={setType} />
        <ListControls range={list.range} day={day} locale={locale} sort={list.sort} onChange={updateList} />
      </div>

      <div className="space-y-3">
        {/* A venue read for an unstamped event (e.g. one of the viewer's own,
            arriving after the region's events) must not swap a rendered list
            back to skeletons — it only holds the freeze above. */}
        {loading || (regionsLoading && scopedEvents.length === 0) ? (
          <section className="space-y-3">
            {placeholders(3).map((_, i) => (
              <EventCard key={i} loading />
            ))}
          </section>
        ) : upcoming.length === 0 ? (
          <EmptyState
            action={<SuggestRow onClick={() => openSuggest('parties_empty')} label={s.suggestLabel} />}
          >
            {s.discoverEmpty}
          </EmptyState>
        ) : listLoading ? (
          placeholders(3).map((_, i) => <EventCard key={i} loading />)
        ) : listed.length > 0 ? (
          <RevealList
            items={listed}
            resetKey={`discovery-${type}-${regionId}-${day ?? list.range}-${list.sort}`}
            renderItem={e => card(e)}
            footer={filtered && (
              <div className="flex flex-col items-center gap-2 py-4 text-sm text-muted-foreground">
                {fbt('No more results for the current filters.', 'Discover list footer below the last event when a type, date range or day filter is applied')}
                <Button variant="outline" size="sm" onClick={resetFilters}>
                  {fbt('Reset filters', 'Discover list footer button clearing the type, date range and day filters')}
                </Button>
              </div>
            )}
          />
        ) : (
          <EmptyState
            action={
              widerRange && (
                <HeaderLink onClick={() => updateList({ range: widerRange })}>
                  {widenRangeLabel(widerRange)}
                </HeaderLink>
              )
            }
          >
            {day ? emptyDayText(day, locale) : emptyRangeText(list.range)}
          </EmptyState>
        )}
      </div>

      <SuggestEventDialog
        open={suggestSource !== null}
        onOpenChange={next => !next && setSuggestSource(null)}
        groups={groups}
        source={suggestSource ?? 'parties_empty'}
        onSubmit={suggestEvent}
      />

    </PageLayout>
  )
}
